import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Módulo Oportunidades (OPOR-I001) — contrato del backend
// (OportunidadesController, api/oportunidades). Recurso ACL único:
// Oportunidades.Gestionar. Las consultas y búsquedas con texto van
// por POST: los nombres y textos buscados nunca viajan en la URL.
// ═══════════════════════════════════════════════════════════════

export const RECURSO_OPORTUNIDADES = 'Oportunidades.Gestionar';

/** Pestañas (R-01). Coincide con `PestaniaOportunidad` del backend. */
export enum Pestania {
  Venta = 1,
  Compra = 2,
  Solicitud = 3,
}

/** Estados de GC_C_ESTADO_OPORTUNIDAD. El 2 se muestra como «Descarte pendiente de confirmar» (R-02 bis). */
export enum EstadoOportunidad {
  Pendiente = 1,
  DescartePendiente = 2,
  Descartada = 3,
  Atendida = 4,
}

export type OrdenListado = 'FECHA' | 'NUMERO' | 'ESTADO' | 'RESPONSABLE';

export interface ListadoQuery {
  pestania: Pestania;
  estados?: number[];
  idResponsable?: number;
  soloPendientesAsignar?: boolean;
  /** yyyy-MM-dd */
  desde?: string;
  hasta?: string;
  idIniciativa?: number;
  idOrigen?: number;
  texto?: string;
  orden?: OrdenListado;
  ordenDescendente?: boolean;
  pagina: number;
  tamanio: number;
}

export interface OportunidadFila {
  idOportunidad: number;
  pestania: Pestania;
  fecha?: string;
  idFarmacia?: number;
  farmacia?: string;
  municipio?: string;
  provincia?: string;
  titulares?: string;
  idCliente?: number;
  cliente?: string;
  iniciativa?: string;
  origen?: string;
  detalle?: string;
  tipoOperacion?: string;
  idEstado: EstadoOportunidad;
  idResponsable?: number;
  responsable?: string;
  pendienteAsignar: boolean;
  /** Viva (1-2) con el responsable de baja (R-04, CA-23). */
  responsableBaja: boolean;
  observaciones?: string;
  operaciones?: string;
}

export interface ListadoPagina {
  filas: OportunidadFila[];
  totalFiltrado: number;
  pagina: number;
  tamanio: number;
  pendientesVenta: number;
  pendientesCompra: number;
  pendientesSolicitud: number;
}

export interface Titular {
  idCliente: number;
  nombre?: string;
  codigo?: string;
}

export interface Operacion {
  idOperacion: number;
  ccl?: string;
  alias?: string;
  fase?: string;
  farmacia?: string;
}

export interface VentaDetalle {
  idOportunidad: number;
  referenciaCcl?: string;
  fechaGeneracion: string;
  fechaObjetivoCaptacion?: string;
  idFarmacia?: number;
  idFarmaciaGf?: number;
  farmacia?: string;
  municipio?: string;
  provincia?: string;
  idTipoOperacion: number;
  idTipoServicio?: number;
  idIniciativa: number;
  idOrigen: number;
  idDetalle?: number;
  idEpcEntidad?: number;
  epcEntidad?: string;
  idEpcPersona?: number;
  epcPersona?: string;
  idFormaContacto: number;
  iniciativa?: string;
  origen?: string;
  detalle?: string;
  observaciones?: string;
  idEstado: EstadoOportunidad;
  motivoDescarte?: string;
  idResponsable?: number;
  responsable?: string;
  pendienteAsignar: boolean;
  /** Viva (1-2) con el responsable de baja (R-04, CA-23). */
  responsableBaja: boolean;
  idGenerador?: number;
  generador?: string;
  firma: string;
  titulares: Titular[];
}

export interface CompraDetalle {
  idOportunidad: number;
  pestania: Pestania;
  fechaSolicitud: string;
  idCliente: number;
  cliente?: string;
  codigoCliente?: string;
  idIniciativa: number;
  idOrigen: number;
  idDetalle?: number;
  idEpcEntidad?: number;
  epcEntidad?: string;
  idEpcPersona?: number;
  epcPersona?: string;
  iniciativa?: string;
  origen?: string;
  detalle?: string;
  observaciones?: string;
  idEstado: EstadoOportunidad;
  motivoDescarte?: string;
  idResponsable?: number;
  responsable?: string;
  pendienteAsignar: boolean;
  /** Viva (1-2) con el responsable de baja (R-04, CA-23). */
  responsableBaja: boolean;
  /** Spec rev. 5 (R-20): vacío en las migradas y en las anteriores a la columna. */
  idGenerador?: number;
  generador?: string;
  firma: string;
  operaciones: Operacion[];
}

export interface ElementoCatalogo {
  id: number;
  descripcion: string;
  idPadre?: number;
}

export interface EmpleadoCatalogo {
  id: number;
  nombre: string;
  asignable: boolean;
}

export interface Catalogos {
  iniciativas: ElementoCatalogo[];
  origenes: ElementoCatalogo[];
  detalles: ElementoCatalogo[];
  estados: ElementoCatalogo[];
  tiposOperacion: ElementoCatalogo[];
  formasContacto: ElementoCatalogo[];
  tiposServicio: ElementoCatalogo[];
  empleados: EmpleadoCatalogo[];
  idOrigenEpc: number;
  /** Tipo de operación «Gestión»: solo con él aplica el servicio (spec rev. 3). */
  idTipoOperacionGestion?: number;
}

export interface EmpleadoUsuario {
  idEmpleado: number;
  nombre: string;
}

export interface FarmaciaCandidata {
  idFarmacia: number;
  idFarmaciaGf?: number;
  descripcion?: string;
  municipio?: string;
  provincia?: string;
  baja: boolean;
  titulares?: string;
}

export interface ClienteCandidato {
  idCliente: number;
  nombre?: string;
  codigo?: string;
}

export interface EpcCandidato {
  id: number;
  nombre?: string;
}

/** Origen, detalle y EPC (R-09): común a alta y edición. */
export interface DatosOrigen {
  idIniciativa: number;
  idOrigen: number;
  idDetalle?: number;
  idEpcEntidad?: number;
  idEpcPersona?: number;
}

export interface AltaVentaRequest extends DatosOrigen {
  idFarmacia?: number;
  idsTitulares?: number[];
  idTipoOperacion: number;
  idTipoServicio?: number;
  idFormaContacto: number;
  observaciones: string;
}

export interface AltaCompraRequest extends DatosOrigen {
  pestania: Pestania.Compra | Pestania.Solicitud;
  idCliente: number;
  observaciones: string;
  idsOperaciones?: number[];
}

export interface AltaResultado {
  idOportunidad: number;
  sinFarmacia: boolean;
}

export interface EditarVentaRequest extends DatosOrigen {
  firma: string;
  idFarmacia?: number;
  idsTitulares?: number[];
  idTipoOperacion: number;
  idTipoServicio?: number;
  idFormaContacto: number;
  fechaGeneracion: string;
  fechaObjetivoCaptacion?: string;
  observaciones: string;
}

export interface EditarCompraRequest extends DatosOrigen {
  firma: string;
  idCliente: number;
  fechaSolicitud: string;
  observaciones: string;
  idsOperaciones?: number[];
}

const tipoRuta = (pestania: Pestania) => (pestania === Pestania.Venta ? 'venta' : 'compra');

export const consultarOportunidades = async (q: ListadoQuery): Promise<ListadoPagina> =>
  (await api.post<ListadoPagina>('/api/oportunidades/consulta', q)).data;

export const leerCatalogos = async (): Promise<Catalogos> =>
  (await api.get<Catalogos>('/api/oportunidades/catalogos')).data;

/** Ficha de empleado de la usuaria (R-15). `null` si no la tiene (204). */
export const leerYo = async (): Promise<EmpleadoUsuario | null> => {
  const r = await api.get<EmpleadoUsuario>('/api/oportunidades/yo');
  return r.status === 204 || !r.data ? null : r.data;
};

export const leerVenta = async (id: number): Promise<VentaDetalle> =>
  (await api.get<VentaDetalle>(`/api/oportunidades/venta/${id}`)).data;

export const leerCompra = async (id: number): Promise<CompraDetalle> =>
  (await api.get<CompraDetalle>(`/api/oportunidades/compra/${id}`)).data;

export const buscarFarmacias = async (texto: string): Promise<FarmaciaCandidata[]> =>
  (await api.post<FarmaciaCandidata[]>('/api/oportunidades/buscar/farmacias', { texto })).data;

export const leerTitularesFarmacia = async (idFarmacia: number): Promise<Titular[]> =>
  (await api.get<Titular[]>(`/api/oportunidades/farmacias/${idFarmacia}/titulares`)).data;

export const buscarClientes = async (texto: string): Promise<ClienteCandidato[]> =>
  (await api.post<ClienteCandidato[]>('/api/oportunidades/buscar/clientes', { texto })).data;

export const buscarOperaciones = async (texto: string): Promise<Operacion[]> =>
  (await api.post<Operacion[]>('/api/oportunidades/buscar/operaciones', { texto })).data;

export const buscarEpc = async (persona: boolean, texto?: string): Promise<EpcCandidato[]> =>
  (await api.post<EpcCandidato[]>('/api/oportunidades/buscar/epc', { persona, texto })).data;

export const crearVenta = async (r: AltaVentaRequest): Promise<AltaResultado> =>
  (await api.post<AltaResultado>('/api/oportunidades/venta', r)).data;

export const crearCompra = async (r: AltaCompraRequest): Promise<AltaResultado> =>
  (await api.post<AltaResultado>('/api/oportunidades/compra', r)).data;

export const asignar = async (pestania: Pestania, id: number, idResponsable: number, notificar: boolean) =>
  (await api.post<{ avisoEncolado: boolean }>(`/api/oportunidades/${tipoRuta(pestania)}/${id}/asignar`, { idResponsable, notificar })).data;

export const cambiarEstado = async (pestania: Pestania, id: number, idEstado: number, motivo?: string): Promise<void> => {
  await api.post(`/api/oportunidades/${tipoRuta(pestania)}/${id}/estado`, { idEstado, motivo });
};

export const editarVenta = async (id: number, r: EditarVentaRequest): Promise<{ firma: string }> =>
  (await api.put<{ firma: string }>(`/api/oportunidades/venta/${id}`, r)).data;

export const editarCompra = async (id: number, r: EditarCompraRequest): Promise<{ firma: string }> =>
  (await api.put<{ firma: string }>(`/api/oportunidades/compra/${id}`, r)).data;
